import React, { Fragment, type FC, type PropsWithChildren, type HTMLAttributes } from 'react';

import { withImage } from '@lucid/hooks';
import kebabCase from 'lodash/kebabCase';

import type { Lucid } from '../../../types/types';
import Text from '../../text/text';
import Card, { type CardElementProps } from '../../card/card';
import Box from '../../box/box';
import Image from '../../image/image';

export interface CarouselCardProps extends Omit<CardElementProps, 'divider'> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Controls styles and video autoplay behavior for the currently highlighted card  */
  isActiveCard?: boolean;
  /** A function to set the active index in the state of the parent component. */
  handleUpdateCarousel: (index: number) => void;
  /** Points displayed beneath the card description */
  bulletPoints?: Lucid.Directus.Repeaters.Card_Carousel_Item_Bullet_Points[];
  /** Position in the carousel */
  index: number;
  /** Label above the integration icons */
  integrationText?: string;
  /** Icons to display at the bottom of the card */
  integrationIcons?: Lucid.Directus.Generated.Directus_Files[];
}

const CarouselCard: FC<PropsWithChildren<CarouselCardProps & HTMLAttributes<HTMLDivElement>>> = ({
  heading,
  link,
  media,
  bulletPoints,
  text,
  index,
  integrationIcons,
  integrationText,
  isActiveCard,
  handleUpdateCarousel,
  ...rest
}) => (
  <Card
    {...rest}
    key={`cardCarousel-${kebabCase(heading)}-${index + 1}`}
    aria-live="polite"
    aria-roledescription="slide"
    css={theme => [
      {
        label: 'carouselCard',
        minWidth: '85vw',
        minHeight: 'min-content',
        cursor: isActiveCard ? 'default' : 'pointer',
        transition: 'box-shadow 0.1s ease-in',
        paddingTop: theme.space.s32,
        paddingBottom: theme.space.s32,
        scrollSnapAlign: 'start',
        scrollSnapStop: 'always',
        [theme.breakpoints.bp500.min]: {
          minWidth: '360px',
        },
      },
    ]}
    data-card-element="carousel-card-container"
    data-testid={`carousel-card-container-${index}`}
    id={`cardCarouselItem-${index}`}
    isActiveCard={isActiveCard}
    onClick={() => handleUpdateCarousel(index)}
    shadow="hover"
  >
    {media && (
      <Card.Media
        {...media}
        aspectRatio="1:1"
        css={[
          { height: '220px', width: '220px', alignSelf: 'center' },
          !isActiveCard && {
            filter: 'grayscale(1)',
          },
        ]}
        willManualOverride={!isActiveCard}
      />
    )}
    {heading && <Card.Heading as="h3" heading={heading} variant="headingM" />}
    {text && <Card.Text {...text} />}
    {bulletPoints?.length && (
      <ul css={theme => ({ margin: `0 0 0 ${theme.space.s32}px` })}>
        {bulletPoints?.map(({ bullet }) => (
          <li key={bullet} css={{ marginBottom: '4px' }}>
            {bullet}
          </li>
        ))}
      </ul>
    )}
    {integrationIcons?.length !== 0 && (
      <>
        <Text css={{ marginBottom: 0 }} variant="label2XS400">
          {integrationText}
        </Text>
        <Box>
          {integrationIcons?.map((item, index) => (
            <Fragment key={`${item?.filename_disk}-${index + 1}`}>
              {item?.filename_disk && (
                <Image
                  key={`${item?.filename_disk}${heading}-icon-${index}`}
                  css={theme => ({ paddingRight: theme.space.s16, height: '30px' })}
                  imageAlt={item?.translations?.[0]?.alt || item.title}
                  imageSrc={withImage(item)}
                />
              )}
            </Fragment>
          ))}
        </Box>
      </>
    )}
    {link?.final_url && <Card.Divider type="default" />}
    {link?.final_url && (
      <Card.Link
        {...link}
        css={theme => ({ fontSize: theme.fontSizes.fs20 })}
        index={index}
        shouldLinkWholeCard={false}
      />
    )}
  </Card>
);

export default CarouselCard;
